import {
  useState,
  memo,
  MutableRefObject,
  useCallback,
  useRef,
  useEffect,
} from "react";
import debounce from "lodash/debounce";
import { pdfjs, Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import type { PDFDocumentProxy } from "pdfjs-dist";
import type { TextItem } from "pdfjs-dist/types/src/display/api.js";
import i18n from "../../i18n";
import { getBrowser } from "../../utils/brower";
import { ReactComponent as EmptyIcon } from "../icons/empty.svg";

import styles from "./index.module.scss";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "@/assets/scripts/pdf.worker.js",
  // "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const options = {
  //替换为阿里云的cmaps
  cMapUrl:
    // "https://sensechat-ml.aoss.cn-sh-01.sensecoreapi-oss.cn/chat/prod/wb/cmaps/",
    "https://sensechatweb.oss-cn-shanghai.aliyuncs.com/chat/prod/wb/cmaps/",
  // standardFontDataUrl: "/standard_fonts/",
};

interface IProps {
  docContainerRef: MutableRefObject<HTMLDivElement | null>;
  docRef: MutableRefObject<any | null>;
  onLoadSuccess: (pageNumber: number) => void;
  url: string;
  scale: number;
}

const reset = (end: HTMLDivElement, textLayer: HTMLDivElement) => {
  if (getBrowser() === "Firefox") {
    textLayer.append(end);
    end.style.width = "";
    end.style.height = "";
  }
  end.classList.remove("active");
};

function PdfDocument({
  docContainerRef,
  docRef,
  onLoadSuccess,
  url,
  scale,
}: IProps) {
  const textLayerEndMap = useRef(new Map());
  const prevRangeRef = useRef<Range>();
  const renderedPageNumber = useRef<number>(0);
  const [numPages, setNumPages] = useState<number>();
  const [width, setWidth] = useState<number>(612);
  function onDocumentLoadSuccess({
    numPages: nextNumPages,
  }: PDFDocumentProxy): void {
    setNumPages(nextNumPages);
  }

  const customTextRenderer = useCallback(
    (
      item: {
        pageIndex: number;
        pageNumber: number;
        itemIndex: number;
      } & TextItem,
    ) => {
      //  font-family:${item.fontName};
      return `<span style="width:${item.width}px;" data-page="${item.pageNumber}" data-item-id="${item.pageNumber}-${item.itemIndex}" class="custom-text">${item.str}</span>`;
    },
    [],
  );

  const renderSuccess = useCallback(() => {
    renderedPageNumber.current = renderedPageNumber.current + 1;
    if (renderedPageNumber.current === numPages) {
      textLayerEndMap.current = new Map();
      const textlayerList = document.querySelectorAll(".textLayer");
      textlayerList.forEach(layer => {
        const end = layer.querySelector(".endOfContent");
        if (end) {
          textLayerEndMap.current.set(layer, end);
        }
      });
      onLoadSuccess(numPages);
    }
  }, [numPages]);

  useEffect(() => {
    if (docContainerRef.current) {
      setWidth(docContainerRef.current.clientWidth);
      const handleResize = debounce(() => {
        if (docContainerRef.current?.clientWidth) {
          renderedPageNumber.current = 0;
          setWidth(docContainerRef.current.clientWidth);
        }
      }, 2000);
      window.addEventListener("resize", handleResize);

      return () => window.removeEventListener("resize", handleResize);
    }
  }, [docContainerRef.current]);

  useEffect(() => {
    const handlePointerup = () => {
      textLayerEndMap.current.forEach(reset);
    };
    const handleSelectionChange = () => {
      const selection = document.getSelection()!;
      if (selection.rangeCount === 0) {
        textLayerEndMap.current.forEach(reset);
        return;
      }

      const activeTextLayers = new Set();
      for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        for (const textLayerDiv of textLayerEndMap.current.keys()) {
          if (
            !activeTextLayers.has(textLayerDiv) &&
            range.intersectsNode(textLayerDiv)
          ) {
            activeTextLayers.add(textLayerDiv);
          }
        }
      }

      for (const [textLayerDiv, endDiv] of textLayerEndMap.current) {
        if (activeTextLayers.has(textLayerDiv)) {
          endDiv.classList.add("active");
        } else {
          reset(endDiv, textLayerDiv);
        }
      }

      if (getBrowser() === "Firefox") {
        return;
      }

      const range = selection.getRangeAt(0);
      const modifyStart =
        prevRangeRef.current &&
        (range.compareBoundaryPoints(Range.END_TO_END, prevRangeRef.current) ===
          0 ||
          range.compareBoundaryPoints(
            Range.START_TO_END,
            prevRangeRef.current,
          ) === 0);
      let anchor = modifyStart ? range.startContainer : range.endContainer;
      if (anchor.nodeType === Node.TEXT_NODE) {
        anchor = anchor.parentNode!;
      }

      const parentTextLayer = anchor.parentElement!.closest(
        ".textLayer",
      ) as HTMLDivElement;
      const endDiv = textLayerEndMap.current.get(parentTextLayer);
      if (endDiv && parentTextLayer) {
        endDiv.style.width = parentTextLayer.style.width;
        endDiv.style.height = parentTextLayer.style.height;
        anchor.parentElement!.insertBefore(
          endDiv,
          modifyStart ? anchor : anchor.nextSibling,
        );
      }

      prevRangeRef.current = range.cloneRange();
    };
    document.addEventListener("pointerup", handlePointerup);
    document.addEventListener("selectionchange", handleSelectionChange);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("pointerup", handlePointerup);
    };
  }, []);

  return (
    <Document
      className={styles.document}
      ref={docRef}
      inputRef={docContainerRef}
      onLoadSuccess={onDocumentLoadSuccess}
      file={url}
      options={options}
      error={
        <div className={styles.errorBox}>
          <EmptyIcon />
          <div className={styles.errorText}>{i18n.t("sensechat-317")}</div>
        </div>
      }
      loading={
        <div className={styles.errorBox}>
          <div>{i18n.t("sensechat-189")}</div>
        </div>
      }
    >
      {Array.from(new Array(numPages), (_el, index) => (
        <Page
          width={width}
          customTextRenderer={customTextRenderer}
          key={`page_${index + 1}`}
          pageNumber={index + 1}
          onRenderTextLayerSuccess={renderSuccess}
          renderAnnotationLayer={false}
          scale={scale}
        ></Page>
      ))}
    </Document>
  );
}

export default memo(PdfDocument);
